import { useContext, useState } from "react";
import IOrderUI from "../../../models/IOrderUI";
import { ControlBar } from "../../shared/ControlBar/ControlBar";
import { IOrderStepButtonsFnCallbacks } from "./IOrderStepButtonsFnCallbacks";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import ISubmitOrderDTO from "../../../models/DTO/ISubmitOrderDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UiContext from "../../../contexts/UiContext";
import { useOrderButtonLogic } from "../../../hooks/order_entry/useOrderStepButtonsLogic";
import OwPrintButton from "../../shared/OwPrintButton/OwPrintButton";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import SupplierService from "../../../services/SupplierService";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import IOrder_Progress_State from "../../../models/IOrder_Progress_State";
import { TimelineNoteModal } from "../TimelineNoteModal/TimelineNoteModal";
import OwToggle from "../../shared/OwToggle/OwToggle";

type Props = {
    order: IOrderUI;
    hasEditMode: boolean;
    hasPendingDetailChanges?: boolean;
    hasPendingFineDetailChanges?: boolean;
    isFineDetailsValid?: boolean;
    hasRowsInEditMode?: boolean;
    btnActive: boolean;
    orderStepBtnsActive: {
        remakeBtnActive: boolean;
        reorderBtnActive: boolean;
    };
    // Callbacks
    callbacks: IOrderStepButtonsFnCallbacks;
};

export const OrderStepButtonsFn = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const buttonLogic = useOrderButtonLogic(
        props.order,
        supplierModel,
        userModel,
        props.hasEditMode,
        props.hasPendingDetailChanges,
        props.hasPendingFineDetailChanges,
        props.isFineDetailsValid,
        props.hasRowsInEditMode
    );

    const [selectedManualOrderStatId, setSelectedManualOrderStatId] = useState<number | undefined>();
    const [addSubmitNote, setAddSubmitNote] = useState(false);

    const selectedManualOrderStatIdFn = (id: number) => {
        setSelectedManualOrderStatId(id);
    };

    const performBtnClick = (nextStep?: IOrder_Progress_State) => {
        // send to the next order state.
        if (nextStep) {
            submitOrder(nextStep.order_state_id);
        }
    };

    const submitOrder = (orderStateId: OrderStatesEnum, note?: string, internalNote?: string) => {
        if (props.order) {
            const payload: ISubmitOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                orderGuid: props.order.guid,
                order_state_id: orderStateId,
            };

            if (note) {
                payload.stateChangeNote = note;
            }

            if (internalNote) {
                payload.stateChangeInternalNote = internalNote;
            }

            props.callbacks.submitOrder(payload);

            // this.setApplicableBtnActive(orderStateId);
        }
    };

    // User Elements - consider moving to a separate component
    const printButton = (
        <div>
            <OwPrintButton />
        </div>
    );

    const viewTimelineButton = (
        <OwButton title={"view_timeline"} leftIconClass={"fad fa-history"} fn={props.callbacks.openTimelineSidebar} />
    );

    // Admin elements - consider moving to a separate component
    const metaDataChangeStep = buttonLogic.showManualOrderStepChange ? (
        <div className={"d-flex justify-content-end align-items-center"}>
            {supplierModel.settings.screens?.order_entry?.hide_meta_data ? null : (
                <div className={"me-2"}>
                    <OwButton title={"edit_metadata"} fn={props.callbacks.editMetaData} leftIconClass={"fas fa-pencil"} />
                </div>
            )}
            <div>
                <div className={"d-flex justify-content-end align-items-center"}>
                    <div className={"me-2"}>
                        <OwSelectOption
                            valueProp={"order_state_id"}
                            titleProp={"name"}
                            name={"order_state_id"}
                            fn={(e: any) => {
                                selectedManualOrderStatIdFn(Number(e.target.value));
                            }}
                            options={buttonLogic.orderSteps}
                            translateTitle={true}
                            showBlankFirst={true}
                        />
                    </div>
                    <div>
                        <OwButton
                            className={"btn-danger"}
                            leftIconClass={"fad fa-random"}
                            title={"change"}
                            fn={() => {
                                if (selectedManualOrderStatId) {
                                    submitOrder(selectedManualOrderStatId);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    ) : null;

    const closeTimelineNoteModal = (nextStep?: IOrder_Progress_State, note?: string, internalNote?: string) => {
        if (nextStep) {
            submitOrder(nextStep.order_state_id, note, internalNote);
            closeModal();
        }
    };

    const showNoteModal = (step?: IOrder_Progress_State) => {
        const modalComponent = (
            <TimelineNoteModal closeModalFn={closeModal} commitModalFn={closeTimelineNoteModal} nextStep={step} userModel={userModel} />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const toggleAddNote = () => {
        setAddSubmitNote(!addSubmitNote);
    };

    const timelineNote =
        !props.order.is_quote && !props.order.is_template && buttonLogic.allowSubmit ? (
            <OwToggle wrapperClassName={"me-2"} fn={toggleAddNote} name={"timeline_note"} label={"timeline_note"} active={addSubmitNote} />
        ) : null;

    const submitBtn =
        buttonLogic.nextStep && buttonLogic.allowSubmit && !props.order.is_on_hold ? (
            <OwButton
                id={"submitButton"}
                disableOnActive={true}
                disabled={buttonLogic.isButtonDisabled || buttonLogic.stepButtonDisabled}
                active={props.btnActive}
                className={"btn-warning"}
                fn={(e: any) => {
                    if (props.order.is_quote) {
                        props.callbacks.performConvertQuoteToOrder();
                    } else {
                        if (addSubmitNote) {
                            showNoteModal(buttonLogic.nextStep);
                        } else {
                            performBtnClick(buttonLogic.nextStep);
                        }
                    }
                }}
                title={buttonLogic.buttonTitle}
                leftIconClass={"fas fa-arrow-circle-right"}
            />
        ) : null;

    const onHoldBtn = buttonLogic.showOnHoldBtn ? (
        <div>
            <OwButton
                leftIconClass={props.order.is_on_hold ? "fas fa-play" : "fas fa-pause"}
                title={props.order.is_on_hold ? "resume_order" : "hold_order"}
                fn={props.callbacks.putOrderOnHoldFn}
                active={props.btnActive}
                disableOnActive={true}
            />
        </div>
    ) : null;

    const markBackorderBtn =
        buttonLogic.showMarkBackorderBtn && uiContext.showHiddenFeatures ? (
            <div>
                <OwButton
                    leftIconClass={"fas fa-truck-loading"}
                    title={props.order.is_backorder ? "unmark_backorder" : "mark_backorder"}
                    fn={() => {
                        props.callbacks.toggleBackorderFn();
                    }}
                    active={props.btnActive}
                    disableOnActive={true}
                />
            </div>
        ) : null;

    const cancelOrderBtn = buttonLogic.showCancelButton ? (
        <div>
            <OwButton
                leftIconClass={"fas fa-cancel"}
                title={SupplierService.updateStringWithOrderNameOverride("cancel_order", supplierModel)}
                fn={() => {
                    props.callbacks.cancelOrderFn();
                }}
                active={props.btnActive}
                disableOnActive={true}
                className={"btn-danger"}
            />
        </div>
    ) : null;

    const uncancelOrderBtn = buttonLogic.showUncancelButton ? (
        <div>
            <OwButton
                leftIconClass={"fas fa-check"}
                title={"uncancel"}
                fn={() => {
                    props.callbacks.cancelOrderFn(true);
                }}
                active={props.btnActive}
                disableOnActive={true}
                className={"btn-danger"}
            />
        </div>
    ) : null;

    const approveOrderBtn = buttonLogic.showApproveDeclineButtons ? (
        <div>
            <OwButton
                className={"btn-success"}
                title={"approve"}
                active={props.btnActive}
                disableOnActive={true}
                fn={() => props.callbacks.approveDeclineDeal("APPROVE")}
            />
        </div>
    ) : null;

    const declineOrderBtn = buttonLogic.showApproveDeclineButtons ? (
        <div>
            <OwButton
                className={"btn-danger"}
                title={"decline"}
                active={props.btnActive}
                disableOnActive={true}
                fn={() => props.callbacks.approveDeclineDeal("DECLINE")}
            />
        </div>
    ) : null;

    const showResendEmailOrderBtn = buttonLogic.showResendEmailOrderBtn ? (
        <div className={""}>
            <OwButton
                title={"resend_email"}
                leftIconClass={"fad fa-envelope"}
                fn={props.callbacks.resendSubmitEmailFn}
                active={props.btnActive}
                disableOnActive={true}
            />
        </div>
    ) : null;

    const allowReorderBtn = buttonLogic.allowReorder ? (
        <div className={""}>
            <OwButton
                title={"reorder"}
                leftIconClass={"fad fa-repeat-alt"}
                fn={() => {
                    props.callbacks.performRemake(true);
                }}
                active={props.orderStepBtnsActive.reorderBtnActive}
                disableOnActive={true}
                className={"btn-secondary"}
            />
        </div>
    ) : null;

    const allowRemakeBtn = buttonLogic.allowRemake ? (
        <OwButton
            active={props.orderStepBtnsActive.remakeBtnActive}
            className={"btn-secondary"}
            leftIconClass={"fad fa-repeat-alt"}
            fn={(e: any) => {
                props.callbacks.performRemake();
            }}
            title={"remake"}
        />
    ) : null;

    const showResendOrderAutomationBtn = buttonLogic.showResendOrderAutomationBtn ? (
        <div>
            <OwButton
                title={"resend_automation_email"}
                leftIconClass={"fad fa-envelope"}
                fn={props.callbacks.resendOrderAutomationFn}
                active={props.btnActive}
                disableOnActive={true}
            />
        </div>
    ) : null;

    const returnToPreliminaryBtn = buttonLogic.allowReturnToPreliminary ? (
        <div className={""}>
            <OwButton
                id={"backToPreliminary"}
                disableOnActive={true}
                active={props.btnActive}
                fn={(e: any) => {
                    performBtnClick({
                        order_state_id: OrderStatesEnum.PRELIMINARY,
                        name: "Preliminary",
                        step_no: 1,
                    });
                }}
                title={"back_to_preliminary"}
                leftIconClass={"fas fa-arrow-circle-left"}
            />
        </div>
    ) : null;

    const userElements: any[] = [printButton, viewTimelineButton];

    const adminElements: any[] = [];

    if (metaDataChangeStep) {
        adminElements.push(metaDataChangeStep);
    }

    const submitElements: any[] = [];

    if (returnToPreliminaryBtn) {
        submitElements.push(returnToPreliminaryBtn);
    }

    if (timelineNote) {
        submitElements.push(timelineNote);
    }

    if (submitBtn) {
        submitElements.push(submitBtn);
    }

    const secondaryBtns = [
        onHoldBtn,
        markBackorderBtn,
        cancelOrderBtn,
        uncancelOrderBtn,
        approveOrderBtn,
        declineOrderBtn,
        showResendEmailOrderBtn,
        showResendOrderAutomationBtn,
        allowReorderBtn,
        allowRemakeBtn,
    ];

    const secondayElements: any[] = [];

    for (const btn of secondaryBtns) {
        if (btn) {
            secondayElements.push(btn);
        }
    }

    return (
        <div className={"OrderStepButtonsFn"}>
            <ControlBar
                name={"Order Step Buttons"}
                sections={[
                    { name: "User", elements: userElements },
                    { name: "Admin", elements: adminElements },
                    { name: "Secondary", elements: secondayElements },
                    { name: "Submit", elements: submitElements },
                ]}
            />
            {buttonLogic.stepButtonDisabled && buttonLogic.stepButtonDisabledReason ? (
                <div>
                    <OwLabel className={"text-danger"} title={buttonLogic.stepButtonDisabledReason} />
                </div>
            ) : null}
        </div>
    );
};
